import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Minus } from "lucide-react";
import Reveal from "@/components/Reveal";
import VerdictStamps from "@/components/VerdictStamps";
import BearingDimensions from "@/components/BearingDimensions";
import BearingSampleDoc from "@/components/BearingSampleDoc";
import BearingTimeline from "@/components/BearingTimeline";
import BearingIntake from "@/components/BearingIntake";

const MAILTO = "mailto:hello@traeva.ie?subject=Bearing%20request&body=Brand%20website%3A%20";

const trustSignals = [
  { v: "8", l: "EU marketplaces assessed" },
  { v: "13 yrs", l: "Operator experience" },
  { v: "30+", l: "Irish brands mapped" },
  { v: "5 days", l: "Request to delivery" },
];

const pricing = [
  {
    id: "P1",
    tag: "Entry level",
    name: "The Orientation",
    price: "€150",
    delivery: "Instant delivery",
    body: "Automated scoring across four dimensions · GO or WAIT · three key data points · no operator review · ideal for early exploration.",
    bg: "#F5F1E8",
    ink: "#141210",
    muted: "#6B6258",
    inkSoft: "#3A3530",
    accent: "#141210",
    isLight: true,
  },
  {
    id: "P2",
    tag: "Most requested",
    name: "The Full Bearing",
    price: "€500",
    delivery: "5 working days",
    body: "Complete six-dimension assessment · operator-reviewed scores · competitor vulnerability map · financial model · risk register · the complete picture.",
    bg: "#0C1C2E",
    ink: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    inkSoft: "rgba(255,255,255,0.85)",
    accent: "#FFFFFF",
    isLight: false,
  },
  {
    id: "P3",
    tag: "Recommended for GO brands",
    name: "The Bearing + Course",
    price: "€950",
    delivery: "5 working days",
    body: "Everything in Full Bearing plus a 12-week week-by-week launch blueprint and a 60-minute founder call to walk the document.",
    bg: "#1F4A35",
    ink: "#FFFFFF",
    muted: "rgba(255,255,255,0.65)",
    inkSoft: "rgba(255,255,255,0.88)",
    accent: "#FFFFFF",
    isLight: false,
  },
  {
    id: "P4",
    tag: "Active partners only",
    name: "Course Corrections",
    price: "€200 / quarter",
    delivery: "Quarterly",
    body: "Quarterly market update for brands already live on Amazon EU. Competitor movement, keyword shifts, new entrants, category drift.",
    bg: "#B85A48",
    ink: "#FFFFFF",
    muted: "rgba(255,255,255,0.7)",
    inkSoft: "rgba(255,255,255,0.9)",
    accent: "#FFFFFF",
    isLight: false,
  },
];

export default function Bearing() {
  return (
    <main data-testid="bearing-page" className="pt-[88px] md:pt-[72px]">

      {/* ───── HERO ───── */}
      <section className="border-b border-line">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20">
          <Reveal>
            <p className="label-mono">/ The Bearing</p>
          </Reveal>

          <Reveal delay={0.05}>
            <h1 className="mt-10 sm:mt-12 font-serif-i text-[40px] sm:text-6xl lg:text-[80px] text-ink leading-[1.04] tracking-tight max-w-5xl text-balance">
              Know exactly where your brand stands — <span className="text-coral">before you move.</span>
            </h1>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="mt-10 sm:mt-12 max-w-xl text-[17px] sm:text-lg text-ink-soft leading-[1.65]">
              A professional market assessment for Irish consumer brands considering Amazon EU.
              Six dimensions scored by a human operator. One document.
              One decision: <span className="text-coral">GO</span>, <span className="text-ink">WAIT</span>, or CONDITIONAL GO.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 sm:mt-12 flex items-center gap-3 text-ink-muted">
              <Minus size={14} strokeWidth={1.5} className="text-coral" />
              <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.22em] uppercase">
                Not a dashboard. Not a report. A professional Bearing.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── TRUST STRIP ───── */}
      <section className="border-b border-line bg-paper">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10 sm:py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
            {trustSignals.map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div data-testid={`trust-${i}`} className="flex flex-col">
                  <p className="font-serif-i text-[26px] sm:text-[30px] text-ink leading-none">{t.v}</p>
                  <p className="mt-3 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-ink-muted leading-snug">
                    {t.l}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── VERDICT STAMPS (GO · CONDITIONAL GO · WAIT) ───── */}
      <VerdictStamps />

      {/* ───── SIX DIMENSIONS ───── */}
      <BearingDimensions />

      {/* ───── SAMPLE BEARING DOCUMENT ───── */}
      <BearingSampleDoc />

      {/* ───── DAY-BY-DAY TIMELINE ───── */}
      <BearingTimeline />

      {/* ───── 12-Q INTAKE PREVIEW ───── */}
      <BearingIntake />

      {/* ───── PRICING ───── */}
      <section className="border-b border-line bg-surface" data-testid="bearing-pricing">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="label-mono">/ The Bearing tiers</p>
            <h2 className="mt-8 sm:mt-10 font-serif-i text-[36px] sm:text-[44px] lg:text-[56px] text-ink leading-[1.05] tracking-tight">
              Choose your depth.
            </h2>
          </Reveal>

          <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {pricing.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <article
                  data-testid={`pricing-${p.id.toLowerCase()}`}
                  className="p-8 sm:p-10 h-full flex flex-col justify-between"
                  style={{ background: p.bg, color: p.ink, minHeight: "440px" }}
                >
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase" style={{ color: p.muted }}>
                      {p.tag}
                    </p>
                    <h3 className="mt-8 font-serif-i text-[28px] sm:text-[32px] leading-tight" style={{ color: p.accent }}>
                      {p.name}
                    </h3>
                  </div>

                  <div>
                    <p className="font-serif-i text-3xl sm:text-4xl leading-none" style={{ color: p.accent }}>
                      {p.price}
                    </p>
                    <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: p.muted }}>
                      {p.delivery}
                    </p>
                    <div className="my-7 h-px" style={{ background: p.isLight ? "#DDD8CE" : "rgba(255,255,255,0.18)" }} />
                    <p className="text-[14px] sm:text-[15px] leading-[1.65]" style={{ color: p.inkSoft }}>
                      {p.body}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── CLOSING CTA ───── */}
      <section data-testid="bearing-cta-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="label-mono">/ Request your Bearing</p>
            <h2 className="mt-8 sm:mt-10 font-serif-i text-[40px] sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight max-w-4xl text-balance">
              Before the conversation — <span className="text-coral">the Bearing.</span>
            </h2>
            <p className="mt-8 sm:mt-10 max-w-xl text-ink-soft leading-[1.65] text-[16px] sm:text-lg">
              Share your brand details. Traeva responds within one business day. Bearings are
              issued selectively — not every enquiry results in a Bearing.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line max-w-3xl">
              <a
                href={MAILTO}
                data-testid="bearing-primary-cta"
                className="md:col-span-2 bg-paper p-7 sm:p-9 flex items-center justify-between gap-6 group hover:bg-ink hover:text-paper transition-colors"
              >
                <span className="font-serif-i text-[22px] sm:text-3xl leading-tight">
                  Request your free Bearing
                </span>
                <ArrowRight size={18} strokeWidth={1.6} className="shrink-0 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/how-it-works"
                data-testid="bearing-secondary-cta"
                className="bg-paper p-7 sm:p-9 flex items-center justify-between gap-4 group hover:bg-surface transition-colors"
              >
                <div>
                  <p className="label-mono">Or</p>
                  <p className="mt-2 font-serif-i text-lg sm:text-xl text-ink leading-tight">
                    See the fares
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={1.6} className="text-coral shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
